import React, { useEffect, useMemo, useState } from "react";

import { useLocation } from "react-router-dom";

import { useSnackbar } from "notistack";

import { InputBase, makeStyles, Paper, Typography } from "@material-ui/core";

import CoinField from "../CoinSwapper/CoinField";
import InvestDialog from "../CoinSwapper/InvestCoinDialog";
import LoadingButton from "../Components/LoadingButton";

import { getBalanceAndSymbol } from "../ethereumFunctions";
import { investToken } from "./InvestFunctions";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    borderRadius: theme.spacing(2),
    width: "100%",
    height: "100%",
    padding: theme.spacing(2),
    display: "flex",
    flexFlow: "column",
    justifyContent: "space-between",
  },
  container_input: {
    padding: theme.spacing(1),
    minHeight: "68px",
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(2),
    borderColor: theme.palette.grey[300],
    borderWidth: "1px",
    borderStyle: "solid",
    width: "100%",
  },
}));
function InvestCard({ network, set_contractInfo, changeHistoryKey }) {
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Stores a record of whether their respective dialog window is open
  const [dialogOpen, setDialogOpen] = useState(false);

  // Stores data about their respective coin
  const [coinIn, setCoinIn] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });

  // Store contract info for read and write data
  const [contractInfo, setContractInfo] = useState({});

  // Stores the current value of their respective text box
  const [field1Value, setField1Value] = useState("");
  const [field2Value, setField2Value] = useState("");

  // Controls the loading button
  const [loading, setLoading] = useState(false);

  const receiveCoinOutValue = useMemo(() => {
    const parsedInput2 = parseFloat(field2Value);

    if (parsedInput2)
      return (contractInfo.annualInterestRate * parsedInput2) / 100;
    return 0;
  }, [field2Value, contractInfo]);

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  let query = useQuery();

  const setReferralCode = () => {
    let referral = query.get("referral");
    let addedReferral = localStorage.getItem("invest_referral");

    if (referral && !addedReferral) {
      localStorage.setItem("invest_referral", referral);
      setField1Value(referral);
    } else {
      if (addedReferral) {
        setField1Value(addedReferral);
      }
    }
  };

  // Called when the dialog window for coin1 exits
  const onTokenSelected = (info) => {
    // Close the dialog window
    setDialogOpen(false);

    // We only update the values if the user provides a token
    if (info && info.contractAddress) {
      set_contractInfo(info);

      setContractInfo(info);

      if (info.disabled) {
        setField2Value("")
      }

      // Getting some token data is async, so we need to wait for the data to return, hence the promise
      getBalanceAndSymbol(
        network.account,
        info.coinIn.address,
        network.wethAddress,
        [info.coinIn]
      ).then((data) => {
        setCoinIn({
          address: info.coinIn.address,
          symbol: info.coinIn.abbr,
          balance: data.balance,
          decimal: data.decimal,
        });
      });
    }
  };

  // Determines whether the button should be enabled or not
  const isButtonEnabled = () => {
    // If both coins have been selected, and a valid float has been entered which is less than the user's balance, then return true
    const parsedInput2 = parseFloat(field2Value);
    return (
      !contractInfo.disabled &&
      coinIn.address &&
      !isNaN(parsedInput2) &&
      0 < parsedInput2 &&
      parsedInput2 <= coinIn.balance
    );
  };

  // Calls the swapTokens Ethereum function to make the swap, then resets nessicary state variables
  const invest = async () => {
    console.log("Attempting to invest tokens...");
    let reseller = field1Value || contractInfo.defaultReseller;
    setLoading(true);

    await investToken(
      reseller,
      field2Value,
      coinIn.address,
      contractInfo.contractAddress,
      contractInfo.investFeeETH,
      network.account,
      (res) => {
        if (res.status) {
          setLoading(false);

          // If the invest was successful, we clear to input to make sure the user doesn't accidental redo the transfer
          setField2Value("");
          enqueueSnackbar("Invest Successful", {
            variant: "success",
            autoHideDuration: 5000,
          });

          setTimeout(() => {
            changeHistoryKey();
          }, 5000);
        } else {
          setLoading(false);
          enqueueSnackbar("Invest Failed (" + res.e.reason + ")", {
            variant: "error",
            autoHideDuration: 5000,
          });
        }
      }
    ).catch((e) => {
      setLoading(false);
      enqueueSnackbar("Invest Failed (" + e.reason + ")", {
        variant: "error",
        autoHideDuration: 5000,
      });
    });
  };

  useEffect(() => {
    const investActiveTimeout = setTimeout(() => {
      if (
        network.investContracts.length &&
        network.investContracts.length == 1
      ) {
        onTokenSelected(network.investContracts[0]);
      } else {
        setDialogOpen(true);
      }
    }, 2000);

    return () => clearTimeout(investActiveTimeout);
  }, [network.investContracts]);

  useEffect(() => {
    setReferralCode();
  }, []);

  return (
    <>
      {/* Dialog Windows */}
      <InvestDialog
        open={dialogOpen}
        onClose={onTokenSelected}
        investContracts={network.investContracts}
        account={network.account}
        network={network}
      />
      <Paper elevation={0} className={classes.paperContainer}>
        <div>
          <Typography variant="h6" className="mb-3">
            Invest Token
          </Typography>

          {field1Value && (
            <div className="mb-3">
              <InputBase
                value={field1Value}
                onChange={(e) => setField1Value(e.target.value)}
                placeholder="Reseller"
                classes={{
                  root: classes.container_input,
                }}
              />
            </div>
          )}

          <div className="mb-3">
            <CoinField
              activeField={!contractInfo.disabled}
              value={field2Value}
              onClick={() => setDialogOpen(true)}
              onChange={(e) => setField2Value(e.target.value)}
              symbol={coinIn.symbol}
              balance={coinIn.balance}
              decimal={coinIn.decimal}
              showMax={!contractInfo.disabled}
              showZiroBalance={true}
            />
          </div>

          {contractInfo?.coinOut?.abbr && (
            <p>
              You Will Receive {receiveCoinOutValue} {contractInfo.coinOut.abbr}{" "}
              Per Year
            </p>
          )}
        </div>

        <LoadingButton
          loading={loading}
          valid={isButtonEnabled()}
          success={false}
          fail={false}
          onClick={invest}
        >
          {loading ? "processing" : "Invest"}
        </LoadingButton>
      </Paper>
    </>
  );
}

export default InvestCard;
